import type { PrototypeBootstrap } from "@peer-start/platform-contracts";
import { SelectField } from "@peer-start/ui";
import { useTranslation } from "react-i18next";
import { ChoicePills } from "./ChoicePills.js";
import { FileDropzone } from "./FileDropzone.js";
import type { OnboardingAnswers } from "./onboarding-answers.js";
import {
  careEnvironments,
  experienceDomains,
  experienceYears,
  workChallenges,
} from "./onboarding-options.js";

type PatchAnswers = (partial: Partial<OnboardingAnswers>) => void;

export function OnboardingAboutStep({
  answers,
  roles,
  onPatch,
}: {
  answers: OnboardingAnswers;
  roles: PrototypeBootstrap["roles"];
  onPatch: PatchAnswers;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-6" aria-labelledby="onboarding-about-title">
      <div>
        <h1 className="text-3xl font-medium tracking-tight" id="onboarding-about-title">
          {t("onboarding.about.title")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("onboarding.about.subtitle")}</p>
      </div>
      <SelectField
        controlId="onboarding.role"
        label={t("onboarding.roleLabel")}
        onChange={(event) => onPatch({ roleId: event.target.value })}
        value={answers.roleId}
      >
        <option disabled value="">
          {t("onboarding.selectOption")}
        </option>
        {roles.map((role) => (
          <option key={role.id} value={role.id}>
            {role.title}
          </option>
        ))}
      </SelectField>
      <SelectField
        controlId="onboarding.context"
        label={t("onboarding.contextLabel")}
        onChange={(event) => onPatch({ careEnvironment: event.target.value })}
        value={answers.careEnvironment}
      >
        <option disabled value="">
          {t("onboarding.contextPlaceholder")}
        </option>
        {careEnvironments.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {t(entry.labelKey)}
          </option>
        ))}
      </SelectField>
      <ChoicePills
        controlId="onboarding.experienceYears"
        label={t("onboarding.experienceLabel")}
        onChange={(value) => onPatch({ years: String(value) })}
        options={experienceYears.map((entry) => ({
          value: entry.value,
          label: t(entry.labelKey),
        }))}
        value={answers.years}
      />
    </section>
  );
}

export function OnboardingCredentialsStep({
  answers,
  onPatch,
}: {
  answers: OnboardingAnswers;
  onPatch: PatchAnswers;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-6" aria-labelledby="onboarding-credentials-title">
      <div>
        <h1 className="text-3xl font-medium tracking-tight" id="onboarding-credentials-title">
          {t("onboarding.credentials.title")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("onboarding.credentials.subtitle")}</p>
      </div>
      <FileDropzone
        controlId="onboarding.certificates"
        dropLabel={t("onboarding.dropFile")}
        emptyLabel={t("onboarding.noFileChosen")}
        fileName={answers.certificateName}
        hint={t("onboarding.credentials.certificatesHint")}
        label={t("onboarding.credentials.certificates")}
        onFile={(file) => onPatch({ certificateName: file?.name ?? null })}
      />
      <FileDropzone
        controlId="onboarding.diplomas"
        dropLabel={t("onboarding.dropFile")}
        emptyLabel={t("onboarding.noFileChosen")}
        fileName={answers.diplomaName}
        hint={t("onboarding.credentials.diplomasHint")}
        label={t("onboarding.credentials.diplomas")}
        onFile={(file) => onPatch({ diplomaName: file?.name ?? null })}
      />
    </section>
  );
}

export function OnboardingExperienceStep({
  answers,
  onPatch,
}: {
  answers: OnboardingAnswers;
  onPatch: PatchAnswers;
}) {
  const { t } = useTranslation();
  return (
    <section className="space-y-6" aria-labelledby="onboarding-experience-title">
      <div>
        <h1 className="text-3xl font-medium tracking-tight" id="onboarding-experience-title">
          {t("onboarding.experience.title")}
        </h1>
        <p className="mt-2 text-muted-foreground">{t("onboarding.experience.subtitle")}</p>
      </div>
      <ChoicePills
        controlId="onboarding.domains"
        label={t("onboarding.domainsLabel")}
        multiple
        onChange={(value) => onPatch({ domainIds: Array.isArray(value) ? value : [value] })}
        options={experienceDomains.map((entry) => ({
          value: entry.value,
          label: t(entry.labelKey),
        }))}
        value={answers.domainIds}
      />
      <label className="block text-sm font-medium" htmlFor="onboarding-missing-topic">
        {t("onboarding.missingTopicLabel")}
        <input
          className="mt-2 block w-full rounded-xl border border-border bg-background p-3 font-normal text-foreground shadow-xs placeholder:text-disabled focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
          data-control-id="onboarding.missingTopic"
          id="onboarding-missing-topic"
          onChange={(event) => onPatch({ missingTopic: event.target.value })}
          placeholder={t("onboarding.missingTopicPlaceholder")}
          value={answers.missingTopic}
        />
      </label>
      <SelectField
        controlId="onboarding.challenge"
        label={t("onboarding.challengeLabel")}
        onChange={(event) => onPatch({ challenge: event.target.value })}
        value={answers.challenge}
      >
        <option disabled value="">
          {t("onboarding.challengePlaceholder")}
        </option>
        {workChallenges.map((entry) => (
          <option key={entry.value} value={entry.value}>
            {t(entry.labelKey)}
          </option>
        ))}
      </SelectField>
    </section>
  );
}
